import { useTranslation } from 'react-i18next';
import OutsideClickHandler from 'react-outside-click-handler';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './CountrySelect.module.css';
import { countryList, getCountryByValue } from './countries';
import arrow from '../../../assets/images/selectArrow.png';

type CountrySelectProps = {
  onUpdate: (value: any) => void,
  label: string
  value?: string,
  error?: boolean,
  required?: boolean,
  isLongLabel?: boolean
}

function CountrySelect(props: CountrySelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string>('');
  const [countries, setCountries] = useState(countryList);
  const [searchInput, setSearchInput] = useState(undefined);

  useEffect(() => {
    if (props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (searchInput) {
      const newCountryList = countryList.filter(country => country.name.includes(searchInput));
      setCountries(newCountryList);
    } else {
      setCountries(countryList);
    }
  }, [searchInput]);

  const outsideClickHandler = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const toggle = () => {
    setIsOpen(prevState => !prevState);
  };

  const optionClickHandler = (optionValue: string) => {
    setValue(optionValue);
    props.onUpdate(optionValue);
    toggle();
  };

  const handleCountrySearch = (event: any) => {
    event.preventDefault();
    setIsOpen(true);
    setValue('');
    setSearchInput(event.target.value.charAt(0).toUpperCase() + event.target.value.slice(1));
  };

  return (
    <div className={styles.container}>
      <label htmlFor='country' className={classNames(
        styles.label, props.isLongLabel && styles.long, { [styles.required]: props.error },
      )}>
        <span>{props.label}</span>
        {props.required && <span className={styles.required}>*</span>}
      </label>
      <OutsideClickHandler onOutsideClick={outsideClickHandler}>
        <div className={classNames(styles.selectInput, { [styles.error]: props.error })}>
          <div className={styles.selectedValue}>
            <input
              id='country'
              className={classNames(value ? styles.value : styles.placeholder, styles.input)}
              placeholder={t('common.placeholder.select')}
              type='text'
              onChange={handleCountrySearch}
              value={value ? getCountryByValue(value) : searchInput}
            />
            <div
              role='button'
              onClick={toggle}
              onKeyDown={toggle}
              tabIndex={0}
              className={styles.arrowContainer}
            >
              <img src={arrow} alt='select arrow'
                   className={isOpen ? styles.arrowUp : styles.arrowDown} />
            </div>
          </div>
          {isOpen && (
            <ul className={styles.listContainer}>
              {countries.map(option =>
                <div
                  role='button'
                  key={option.value}
                  onClick={() => optionClickHandler(option.value)}
                  tabIndex={0}
                  onKeyDown={() => optionClickHandler(option.value)}
                >
                  <li>
                    {option.name}
                  </li>
                </div>,
              )}
            </ul>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
}

export default CountrySelect;
